import { datadogLogs } from "@datadog/browser-logs";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { LOCAL_STORAGE_KEYS } from "src/constants";
import { queryClient } from "src/libs";
import { oktaAuthTokenDecoder } from "src/services/oktaAuth.tokenDecoder";
import { getMyLoginFromIdentityToken, localStorage } from "src/utils";

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      const setAuthStoreState = (props: SetAuthStoreTokensProps) => {
        set(mapToAuthStoreState(props));
      };

      const clearAuthStoreState = () => {
        set({ token: null, loginFromNewAuthProvider: null, isOktaLoginEnabled: false });
      };

      const logout = async () => {
        const { isOktaLoginEnabled } = get();

        // If user is logged in with Okta, use Okta's signOut
        if (isOktaLoginEnabled) {
          try {
            const { oktaAuthService } = await import("src/services/oktaAuth.service");
            await oktaAuthService.signOut();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Okta signOut error: ${errorMessage}`);
          }
        }

        clearAuthStoreState();
        queryClient.clear();
        datadogLogs.clearUser();
      };

      return {
        token: null,
        loginFromNewAuthProvider: null,
        isOktaLoginEnabled: false,
        setAuthStoreState,
        logout,
      };
    },
    {
      name: LOCAL_STORAGE_KEYS.AUTH_STORAGE,
      storage: createJSONStorage(() => ({
        setItem: (key: string, value: string) => localStorage.set(key as LOCAL_STORAGE_KEYS, value),
        getItem: (key: string) => localStorage.get(key as LOCAL_STORAGE_KEYS),
        removeItem: (key: string) => localStorage.remove(key as LOCAL_STORAGE_KEYS),
      })),
    }
  )
);

const mapToAuthStoreState = ({
  accessToken,
  idToken,
  isOktaLoginEnabled = false,
}: SetAuthStoreTokensProps) => ({
  token: accessToken,
  loginFromNewAuthProvider:
    isOktaLoginEnabled && idToken
      ? getMyLoginFromIdentityToken(idToken, oktaAuthTokenDecoder)
      : null,
  isOktaLoginEnabled,
});

type AuthState = {
  token: string | null;
  loginFromNewAuthProvider: string | null;
  isOktaLoginEnabled: boolean;
  setAuthStoreState: ({
    accessToken,
    idToken,
    isOktaLoginEnabled,
  }: SetAuthStoreTokensProps) => void;
  logout: () => Promise<void>;
};

type SetAuthStoreTokensProps = {
  accessToken: string;
  idToken?: string;
  isOktaLoginEnabled?: boolean;
};
